/**
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細の調査メモ 2 セクション。(1) memo スカラーの in-place 編集（1 依頼 1 文字列、editable のみ textarea + 保存。保存成功後はドラフトが保存値のままの間、保存ボタン横に緑のチェックを出す）と (2) notes タイムライン（append-only。記入者 · 日時 + クランプ本文、editable のみ追記入力）を描画する。翻訳中は shownMemo / shownNotes に訳文が入る。ops.requests.$id 本体から切り出した表示 + ローカル編集専用コンポーネント
 * @graph-connects OpsDetailPage [references] 依頼詳細ページ本体から調査メモ / notes タイムライン UI を分離
 * @graph-connects useSetOpsRequestMemo [calls] 調査メモ（memo スカラー）編集（PATCH）の実行
 * @graph-connects useAddOpsNote [calls] 調査メモ（notes タイムライン）追記（POST）の実行
 */
import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { OpsNoteRow } from './OpsNoteRow.js';
import { useAddOpsNote, useSetOpsRequestMemo } from '../../../hooks/useOps.js';
import type { OpsRequest } from '../../../types/ops.js';

/** 調査メモ 2 セクション（memo 編集 + notes タイムライン）の props。 */
export interface OpsMemoAndNotesProps {
  /** 表示対象の依頼（id / memo / notes を参照）。 */
  request: OpsRequest;
  /** Firestore 発番（編集可能）依頼かどうか。false=legacy 読み取り専用。 */
  editable: boolean;
  /** 表示する調査メモ（翻訳中は訳文、通常は request.memo）。read-only 表示に使う。 */
  shownMemo: string | null;
  /** 表示する notes 本文（request.notes と同じ順。翻訳中は訳文配列）。 */
  shownNotes: string[];
  /** 翻訳関数。 */
  t: TFunction;
}

/**
 * 調査メモ（memo スカラー）の編集セクションと notes タイムラインのセクションを描画する。memoDraft /
 * noteDraft のローカル編集 state と、memo 保存 / note 追記の mutation をこのコンポーネントが保持する。
 * memo 保存の成否表示（成功チェック）は mutation の状態とドラフトから導出し、別 state は持たない。
 * @graph-connects none
 */
export function OpsMemoAndNotes({
  request,
  editable,
  shownMemo,
  shownNotes,
  t,
}: OpsMemoAndNotesProps): ReactNode {
  const setMemoMutation = useSetOpsRequestMemo();
  const addNoteMutation = useAddOpsNote();
  // 調査メモ（memo スカラー）編集ドラフト。1 依頼 1 文字列を in-place で編集する。
  const [memoDraft, setMemoDraft] = useState(request.memo ?? '');
  // notes タイムラインへの追記ドラフト（append-only。成功で空へ戻す）。
  const [noteDraft, setNoteDraft] = useState('');

  // 調査メモ（memo スカラー）を保存する。空文字は null（消去）として送る。編集可能な依頼のみ。
  const saveMemo = (): void => {
    if (!editable) return;
    const trimmed = memoDraft.trim();
    setMemoMutation.mutate({ id: request.id, memo: trimmed === '' ? null : trimmed });
  };

  // 保存成功後、ドラフトが保存した値のままの間だけチェックを出す（再編集した瞬間に消え、未保存と区別できる）。
  const draftMemo = memoDraft.trim() === '' ? null : memoDraft.trim();
  const memoSaved = setMemoMutation.isSuccess && setMemoMutation.variables?.memo === draftMemo;

  // notes タイムラインへメモを 1 件追記する。空白のみは送らず、成功でドラフトを空へ戻す。編集可能な依頼のみ。
  const addNote = (): void => {
    const trimmed = noteDraft.trim();
    if (!editable || trimmed === '') return;
    addNoteMutation.mutate(
      { id: request.id, text: trimmed },
      {
        onSuccess: () => {
          setNoteDraft('');
        },
      },
    );
  };

  return (
    <>
      {/* 調査メモ（memo スカラー）— 1 依頼 1 文字列を in-place 編集する。編集可能な依頼だけ textarea + 保存を出す。 */}
      <Card>
        <CardHeader title={t('ops.detail.investigationMemo')} />
        <CardContent>
          {editable ? (
            <div className="space-y-2">
              <Textarea
                aria-label={t('ops.detail.memoLabel')}
                value={memoDraft}
                onChange={(e) => {
                  setMemoDraft(e.target.value);
                }}
                placeholder={t('ops.detail.memoPlaceholder')}
                rows={4}
                disabled={setMemoMutation.isPending}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={setMemoMutation.isPending}
                  onClick={saveMemo}
                >
                  {t('ops.detail.memoSave')}
                </Button>
                {/* live region は常時置く（後から挿入すると読み上げられない環境がある）。中身だけ切り替える。 */}
                <span role="status" className="text-sm text-[var(--color-success)]">
                  {memoSaved && (
                    <>
                      <span aria-hidden="true">✓</span>
                      <span className="sr-only">{t('ops.detail.memoSaved')}</span>
                    </>
                  )}
                </span>
                {setMemoMutation.isError && (
                  <span className="text-xs text-[var(--color-error)]">
                    {t('ops.detail.memoSaveFailed')}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text-secondary)]">
              {shownMemo !== null && shownMemo !== '' ? shownMemo : t('ops.detail.noMemo')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* 調査メモ（notes タイムライン）— append-only。各行は記入者 · 日時 + クランプ本文（もっと見るで展開）。 */}
      <Card>
        <CardHeader title={t('ops.detail.investigationNotes')} />
        <CardContent className="space-y-3">
          {request.notes.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('ops.detail.noNotes')}</p>
          ) : (
            <div className="space-y-2">
              {request.notes.map((n, i) => (
                <OpsNoteRow key={n.id} note={n} text={shownNotes[i] ?? n.text} t={t} />
              ))}
            </div>
          )}
          {editable && (
            <div className="space-y-2 border-t border-[var(--color-border)] pt-3">
              <Textarea
                aria-label={t('ops.detail.addNotePlaceholder')}
                value={noteDraft}
                onChange={(e) => {
                  setNoteDraft(e.target.value);
                }}
                placeholder={t('ops.detail.addNotePlaceholder')}
                rows={3}
                disabled={addNoteMutation.isPending}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={addNoteMutation.isPending || noteDraft.trim() === ''}
                  onClick={addNote}
                >
                  {t('ops.detail.addNoteButton')}
                </Button>
                {addNoteMutation.isError && (
                  <span className="text-xs text-[var(--color-error)]">
                    {t('ops.detail.addNoteFailed')}
                  </span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
